import { useEffect, useLayoutEffect, useRef, type ComponentProps, type ReactNode } from "react";
import { useLocation } from "react-router";
import { styled } from "@pigment-css/react";
import { useOptionalShellChrome } from "./ShellProvider";
import { useHasOverlay } from "./useShellDismiss";
import { ShellSlotHost } from "./ShellSlotHost";

// The covered page stays mounted: the overlay's owner renders inside this subtree, so
// unmounting it would dismiss the overlay it hosts. Hidden via visibility and a zeroed height
// rather than display: none, so self-measuring content (Collapse) keeps its size and the
// document regains its full height in the same commit that closes the overlay.
const Region = styled("div")({
  '&[data-behind-overlay="true"]': {
    flex: "0 0 0px",
    minHeight: 0,
    height: 0,
    overflow: "clip",
    visibility: "hidden",
  },
});

interface ShellContentProps {
  className?: string;
  ref?: ComponentProps<typeof Region>["ref"];
  children: ReactNode;
}

/**
 * The shell's content area plus the overlay layer next to it. Hidden, not unmounted, while an
 * overlay is open. Subscribes on its own so an overlay opening leaves `children` untouched and
 * React bails out of re-rendering the routed page. On close it restores the window scroll the
 * page had when the overlay opened.
 */
export function ShellContent({ className, ref, children }: ShellContentProps) {
  const chrome = useOptionalShellChrome();
  const hasOverlay = useHasOverlay();
  const { key: locationKey } = useLocation();
  const covered = useRef<{ scrollY: number; locationKey: string } | null>(null);
  const locationKeyRef = useRef(locationKey);
  locationKeyRef.current = locationKey;

  // The subscriber fires from the claim itself, before React commits the viewport clamp that
  // zeroes the window scroll; by the time a render sees hasOverlay, scrollY already reads 0.
  useEffect(
    () =>
      chrome.store.subscribe((state, previous) => {
        if (previous.regions.overlay.ownerId === null && state.regions.overlay.ownerId !== null)
          covered.current = { scrollY: window.scrollY, locationKey: locationKeyRef.current };
      }),
    [chrome],
  );

  // Restore only once the clamp is out of the DOM, or scrollTo snaps back to 0. An overlay can
  // also close by navigation (tapping another tab unmounts its owner): then the offset belongs
  // to the previous page and ScrollRestoration owns the scroll, so a changed location drops it.
  useLayoutEffect(() => {
    if (hasOverlay || covered.current === null) return;
    const { scrollY, locationKey: coveredLocationKey } = covered.current;
    covered.current = null;
    if (coveredLocationKey === locationKey) window.scrollTo(0, scrollY);
  }, [hasOverlay, locationKey]);

  return (
    <>
      <Region className={className} ref={ref} data-behind-overlay={hasOverlay || undefined}>
        {children}
      </Region>
      <ShellSlotHost slot="overlay" />
    </>
  );
}
